import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Per Quoting_markdown/dustskirts.md — three fixed-price styles (Shirred/
// Tailored/Box Pleated) looked up by Bed Size, so unlike most window/soft-
// goods forms there's no dimension input at all: Bed Size + Style alone
// determine Fabric/Interlining/Lining/Fabrication (calcDustskirt in
// sopCalculations.ts). Drop is work-order-only (no pricing effect) — its own
// dedicated drop_inches column (migration 066), not a repurposed generic
// width/height column, matching the precedent set by Traversing Drape's Hem
// Size / Roman Shade's Flap Size.

type BedSize = 'twin' | 'full' | 'queen' | 'king' | '';
type DustskirtStyle = 'shirred' | 'tailored' | 'box_pleated' | '';

interface State {
  bedSize: BedSize;
  dustskirtStyle: DustskirtStyle;
  fabricDescription: string;
  dropInches: string;
}

const DustskirtForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [state, setState] = useState<State>({
    bedSize: (iv?.bedSize as BedSize) || '',
    dustskirtStyle: (iv?.dustskirtStyle as DustskirtStyle) || '',
    fabricDescription: iv?.fabricDescription || '',
    dropInches: iv?.dropInches != null ? String(iv.dropInches) : '',
  });
  const [error, setError] = useState<string | null>(null);
  const s = (patch: Partial<State>) => setState((prev) => ({ ...prev, ...patch }));

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!state.bedSize || !state.dustskirtStyle) {
        setError('Bed Size and Style are required');
        return null;
      }
      setError(null);
      return {
        bedSize: state.bedSize,
        dustskirtStyle: state.dustskirtStyle,
        fabricDescription: state.fabricDescription.trim() || null,
        dropInches: state.dropInches ? parseFloat(state.dropInches) : null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Bed Size</label>
          <select
            className="input"
            value={state.bedSize}
            onChange={(e) => s({ bedSize: e.target.value as BedSize })}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="twin">Twin</option>
            <option value="full">Full</option>
            <option value="queen">Queen</option>
            <option value="king">King</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Style</label>
          <select
            className="input"
            value={state.dustskirtStyle}
            onChange={(e) => s({ dustskirtStyle: e.target.value as DustskirtStyle })}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="shirred">Shirred</option>
            <option value="tailored">Tailored</option>
            <option value="box_pleated">Box Pleated</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Drop (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number" min="0" step="0.25" className="input"
            value={state.dropInches}
            onChange={(e) => s({ dropInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={state.fabricDescription}
            onChange={(name) => s({ fabricDescription: name })}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default DustskirtForm;
